import React from 'react'

import { useTranslations } from '@dfb/gatsby-dfb-theme/src/hooks/useTranslations'
import DfbHeadline from '@dfb/gatsby-dfb-theme/src/components/ui-kit/dfb_headline'
import DfbInput from '@dfb/gatsby-dfb-theme/src/components/ui-kit/dfb_input'
import DfbParagraph from '@dfb/gatsby-dfb-theme/src/components/ui-kit/dfb_paragraph'
import DfbRte from '@dfb/gatsby-dfb-theme/src/components/ui-kit/dfb_rte'
import { FieldErrors, UseFormRegister } from 'react-hook-form'
import DfbInputTextarea from '@dfb/gatsby-dfb-theme/src/components/ui-kit/dfb_input_textarea'
import DfbSelect from '@dfb/gatsby-dfb-theme/src/components/ui-kit/dfb_select'
import { checkRequireLabel } from '../../../../../utils/form/checkRequireLabel'
import { emailRegex } from '../../../../../utils/form/emailRegex'
import MDefaultform from './m_defaultForm'

export type FormFieldType = 'string' | 'message' | 'postCode' | 'email' | 'phone' | 'dropdown' | 'privacy' | 'number'

export type FormField = {
  type: FormFieldType
  label: string
  hint?: string
  required: boolean
  identifier: string
  fullSize: boolean
  options?: Array<string>
}

export type FormSection = {
  title: string
  fields: Array<Array<FormField>>
}

export type FormConfiguration = {
  buttonLabel: string
  postUrl: string
  captcha: boolean
  identifier: string
  formType: string
  persist: boolean
  headline: string
  text: string
  sections: Array<FormSection>
}

const Privacy = () => {
  const t = useTranslations()
  return (
    <div className="m-Formblock-input">
      <DfbParagraph text={t('form.privacyText')} add_class="m-Formblock-text" />
    </div>
  )
}
const StringField = ({
  register,
  error,
  errorMessage,
  validators,
  label,
  index,
  formItem,
  className,
  type = 'text',
  hint,
}: {
  register: UseFormRegister<FormField>
  error: boolean
  errorMessage?: any
  validators?: any
  label: string
  index: number
  formItem: any
  className: string
  type: string
  hint?: string
}) => {
  return (
    <div className={className} key={index}>
      <DfbInput
        {...register(formItem.identifier, validators)}
        error={error}
        errorMessage={errorMessage}
        label={label}
        focused
        type={type}
      />
      {hint && <p style={{ color: '#757678', margin: '0.5rem 0', fontSize: '0.9rem' }}>{hint}</p>}
    </div>
  )
}

const MessageField = ({
  register,
  error,
  errorMessage,
  validators,
  label,
  formItem,
}: {
  register: UseFormRegister<FormField>
  error: boolean
  errorMessage?: any
  validators?: any
  label: string
  formItem: any
  className: string
}) => {
  return (
    <div className="m-Formblock-input dfb-Input--multiline">
      <DfbInputTextarea
        {...register(formItem.identifier, validators)}
        error={error}
        errorMessage={errorMessage}
        label={label}
        maxLength={200}
        tagName="textarea"
      />
    </div>
  )
}

const DropdownField = ({
  register,
  error,
  errorMessage,
  validators,
  label,
  formItem,
}: {
  register: UseFormRegister<FormField>
  error: boolean
  errorMessage?: any
  validators?: any
  label: string
  formItem: any
  className: string
}) => {
  const t = useTranslations()
  return (
    <div className="m-Formblock-input">
      <DfbSelect
        {...register(formItem.identifier, validators)}
        label={label}
        placeholder={t('form.placeholder.select')}
        options={formItem.options.map((name) => {
          return { name: name, identifier: name }
        })}
        errorMessage={errorMessage}
        error={error}
      />
    </div>
  )
}

const FormField = ({
  register,
  errors,
  formField,
  className,
}: {
  register: UseFormRegister<FormField>
  errors: FieldErrors
  formField: FormField
  className: string
}) => {
  const t = useTranslations()
  const validators = {}
  let type = 'string'

  if (formField.required) {
    validators['required'] = t('form.errorMessage.input')
  }

  switch (formField.type) {
    case 'privacy':
      return <Privacy />
    case 'dropdown':
      return (
        <DropdownField
          validators={validators}
          register={register}
          error={!!errors[formField.identifier]}
          errorMessage={errors?.[formField.identifier]?.message}
          formItem={formField}
          className={className}
          label={checkRequireLabel(formField)}
        />
      )
    case 'message':
      return (
        <MessageField
          validators={validators}
          register={register}
          error={!!errors[formField.identifier]}
          errorMessage={errors?.[formField.identifier]?.message}
          formItem={formField}
          className={className}
          label={checkRequireLabel(formField)}
        />
      )
    case 'number':
      type = 'number'
      validators['pattern'] = {
        value: /^\d+$/,
        message: t('form.errorMessage.numberPattern'),
      }
      break
    case 'postCode':
      validators['pattern'] = {
        value: /^\d+$/,
        message: t('form.errorMessage.postcodePattern'),
      }
      break
    case 'email':
      validators['pattern'] = {
        value: emailRegex,
        message: t('form.errorMessage.emailPattern'),
      }
      break
    case 'phone':
    case 'string':
  }

  return (
    <StringField
      validators={validators}
      register={register}
      error={!!errors[formField.identifier]}
      errorMessage={errors?.[formField.identifier]?.message}
      formItem={formField}
      className={className}
      label={checkRequireLabel(formField)}
      index={1}
      type={type}
      hint={formField.hint}
    />
  )
}

const FormSection = ({
  section,
  register,
  errors,
}: {
  section: FormSection
  register: UseFormRegister<FormField>
  errors: FieldErrors
}) => {
  return (
    <div>
      <div className="m-Formblock-spacer"></div>
      <DfbHeadline headline={section.title} headline_level="h4" add_class="m-Formblock-headline" testid="journey-key-data-label" />
      {section.fields.map((fields) => (
        <div className={fields.length === 3 ? 'm-Formblock-row-third' : 'm-Formblock-input-half'}>
          {fields.map((field) => (
            <FormField register={register} errors={errors} formField={field} className={fields.length === 3 ? '' : 'm-Formblock-input'} />
          ))}
        </div>
      ))}
    </div>
  )
}

const MConfigurableForm = ({ config }: { config: FormConfiguration }) => {
  return (
    <MDefaultform
      buttonLabel={config.buttonLabel}
      postUrl={config.postUrl}
      disableCaptcha={!config.captcha}
      identifier={config.identifier}
      formType={config.formType}
      ShouldPersist={config.persist}
    >
      {({ register, errors }) => (
        <>
          {config.headline && <DfbHeadline headline={config.headline} headline_level="h3" add_class="m-Formblock-headline" />}
          {config.text && <DfbRte text={config.text} add_class="m-Formblock-text" />}
          {config.sections.map((section) => (
            <FormSection section={section} register={register} errors={errors} />
          ))}
        </>
      )}
    </MDefaultform>
  )
}

export default MConfigurableForm
